/**
 * Hook that orchestrates the anomaly pipeline REST flow + reducer transitions.
 *
 * Stale-response guard: each call increments `epochRef`; responses whose
 * captured epoch no longer matches the latest are dropped (typical case:
 * RESET happens while a request is in flight, or user double-clicks Start).
 */
import { useCallback, useEffect, useReducer, useRef } from 'react';
import i18n from 'i18next';
import { anomalyApi } from './anomalyApi';
import {
  anomalyReducer,
  initialAnomalyState,
  AnomalyState,
  AnomalyAction,
} from './anomalyReducer';
import { AnomalyParameters, Lang } from './types';
import { log } from '@/core/debug/anomalyLogger';

/**
 * Convert a thrown error into a structured payload for the FSM. Reads
 * `code`/`suggestion`/`status` augmentations attached by `anomalyApi.postJson`
 * (T5). The reducer stores the payload; the render layer translates via the
 * mapper at render time so language switches re-translate.
 */
export function errorPayloadFromError(err: unknown): {
  code: string | undefined;
  suggestion: string | undefined;
  fallback: string;
  persistent: boolean;
} {
  const e = err as Error & { code?: string; suggestion?: string; status?: number };
  if (e?.name === 'ShapeError') {
    return {
      code: undefined,
      suggestion: undefined,
      fallback: i18n.t('dataProcessing:anomaly.errors.unexpectedResponse', {
        defaultValue:
          'The server returned unexpected data. Please reload the page; if the problem persists, contact support.',
      }),
      persistent: false,
    };
  }
  return {
    code: e?.code,
    suggestion: e?.suggestion,
    fallback: e?.message ?? i18n.t('dataProcessing:anomaly.errors.unknownError', {
      defaultValue: 'Unknown error',
    }),
    // Keep actionable errors visible until dismissed: any backend rejection —
    // one with a structured code, OR a 4xx/5xx HTTP status carrying a message
    // that tells the user what to fix (e.g. the STL "no run has >= 2 periods"
    // 400). Exception: 409 "pipeline busy" is retryable, so it may auto-dismiss.
    // Network errors (no status, no code) also auto-dismiss.
    persistent:
      typeof e.code === 'string' ||
      (typeof e.status === 'number' && e.status !== 409),
  };
}

/**
 * Build a threshold-validation-failed payload. When the BE rejects with
 * "already running" (concurrent threshold submission), surface a friendly
 * "retry shortly" message instead of the raw backend text.
 */
function thresholdErrorPayload(err: unknown): {
  code: string | undefined;
  suggestion: string | undefined;
  fallback: string;
  persistent: boolean;
} {
  const e = err as Error;
  const isBusy = e instanceof Error && e.message.includes('already running');
  if (isBusy) {
    return {
      code: undefined,
      suggestion: undefined,
      fallback: i18n.t('dataProcessing:anomaly.errors.pipelineBusy', {
        defaultValue: 'Pipeline busy — please retry in a few seconds.',
      }),
      persistent: false,
    };
  }
  return errorPayloadFromError(err);
}

export interface UseAnomalyPipeline {
  state: AnomalyState;
  dispatch: React.Dispatch<AnomalyAction>;
  load: (uploadId: string, filename: string, lang: Lang) => Promise<void>;
  start: (params: AnomalyParameters, lang: Lang) => Promise<void>;
  submitStlThreshold: (threshold: number, lang: Lang) => Promise<void>;
  submitLstmThreshold: (threshold: number, lang: Lang) => Promise<void>;
  useProcessedAsOriginal: (lang: Lang) => Promise<boolean>;
  setParams: (params: AnomalyParameters) => void;
  reset: () => void;
  cancel: () => Promise<void>;
}

export function useAnomalyPipeline(): UseAnomalyPipeline {
  const [state, dispatch] = useReducer(anomalyReducer, initialAnomalyState);

  // Refs avoid stale closures over state.uploadId / state.columnName when
  // a request is in flight and the user navigates away or resets.
  const uploadIdRef = useRef<string | null>(null);
  const columnNameRef = useRef<string | null>(null);
  const epochRef = useRef(0);
  // AbortController ref — holds the controller for the current in-flight /start
  // request so cancel() can abort it immediately.
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    uploadIdRef.current = state.uploadId;
    columnNameRef.current = state.columnName;
  }, [state.uploadId, state.columnName]);

  const newEpoch = () => ++epochRef.current;

  const load = useCallback(async (uploadId: string, filename: string, lang: Lang) => {
    log.hook('useAnomalyPipeline', 'load called', { uploadId, filename, lang });
    const epoch = newEpoch();
    dispatch({ type: 'UPLOAD_STARTED', uploadId, filename });
    try {
      log.hook('useAnomalyPipeline', 'load awaiting anomalyApi.load');
      const res = await anomalyApi.load(uploadId, filename, lang);
      if (epoch !== epochRef.current) {
        log.hook('useAnomalyPipeline', 'load stale response, ignoring');
        return;
      }
      log.hook('useAnomalyPipeline', 'load ok', { columnName: res.columnName, rowCount: res.rowCount });
      dispatch({
        type: 'LOADED',
        plots: { original: res.plots.original, slope: res.plots.slope },
        columnName: res.columnName,
        rowCount: res.rowCount,
        timestepMin: res.timestepMin,
        offsetMin: res.offsetMin,
      });
    } catch (e) {
      if (epoch !== epochRef.current) return;
      log.err('useAnomalyPipeline.load', e);
      dispatch({ type: 'UPLOAD_FAILED', error: errorPayloadFromError(e) });
    }
  }, []);

  const start = useCallback(async (params: AnomalyParameters, lang: Lang) => {
    const id = uploadIdRef.current;
    log.hook('useAnomalyPipeline', 'start called', { uploadId: id, lang });
    if (!id) {
      // Defensive: FSM should disable Start before /load completes.
      // Log dev-side, do not surface as user-visible error.
      log.err('useAnomalyPipeline.start', 'start() called without uploadId');
      return;
    }
    // Abort any previous in-flight /start before launching a new one.
    if (abortRef.current) {
      log.hook('useAnomalyPipeline', 'start aborting previous controller');
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    const epoch = newEpoch();
    dispatch({ type: 'START_REQUESTED', params });
    try {
      log.hook('useAnomalyPipeline', 'start awaiting anomalyApi.start');
      const res = await anomalyApi.start(id, params, lang, { signal: controller.signal });
      abortRef.current = null;
      if (epoch !== epochRef.current) {
        log.hook('useAnomalyPipeline', 'start stale response, ignoring');
        return;
      }
      log.hook('useAnomalyPipeline', 'start ok', { status: res.status });
      if (res.status === 'awaiting_stl_threshold') {
        dispatch({ type: 'AWAITING_STL', plots: res.plots, stlCoverage: res.stlCoverage ?? null });
      } else if (res.status === 'awaiting_lstm_threshold') {
        dispatch({ type: 'AWAITING_LSTM', plots: res.plots });
      } else {
        dispatch({
          type: 'COMPLETE',
          plots: res.plots,
          processedCsvFilename: res.processedCsvFilename ?? null,
        });
      }
    } catch (e) {
      abortRef.current = null;
      if (epoch !== epochRef.current) return;
      log.err('useAnomalyPipeline.start', e);
      dispatch({ type: 'PIPELINE_ERROR', error: errorPayloadFromError(e) });
    }
  }, []);

  const submitStlThreshold = useCallback(async (threshold: number, lang: Lang) => {
    const id = uploadIdRef.current;
    log.hook('useAnomalyPipeline', 'submitStlThreshold called', { uploadId: id, threshold, lang });
    if (!id) return;
    // Abort any previous in-flight threshold request before launching a new one.
    if (abortRef.current) {
      log.hook('useAnomalyPipeline', 'submitStlThreshold aborting previous controller');
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    const epoch = newEpoch();
    dispatch({ type: 'APPLYING_STL' });
    try {
      log.hook('useAnomalyPipeline', 'submitStlThreshold awaiting anomalyApi.stlThreshold');
      const res = await anomalyApi.stlThreshold(id, threshold, lang, { signal: controller.signal });
      abortRef.current = null;
      if (epoch !== epochRef.current) {
        log.hook('useAnomalyPipeline', 'submitStlThreshold stale response, ignoring');
        return;
      }
      log.hook('useAnomalyPipeline', 'submitStlThreshold ok', { status: res.status });
      if (res.status === 'awaiting_lstm_threshold') {
        dispatch({ type: 'AWAITING_LSTM', plots: res.plots });
      } else {
        dispatch({
          type: 'COMPLETE',
          plots: res.plots,
          processedCsvFilename: res.processedCsvFilename ?? null,
        });
      }
    } catch (e) {
      abortRef.current = null;
      if (e instanceof Error && e.name === 'AbortError') {
        // Cancelled by user — cancel flow handles its own dispatch.
        return;
      }
      if (epoch !== epochRef.current) return;
      log.err('useAnomalyPipeline.submitStlThreshold', e);
      // Threshold validation rejected by server — keep modal open for retry
      // (Markus's L1260-1271 requirement). Special-case 409 stale-lock
      // recovery: tell the user to wait, rather than confusing them with
      // "Pipeline already running" while they're entering a threshold.
      dispatch({ type: 'THRESHOLD_VALIDATION_FAILED', error: thresholdErrorPayload(e) });
    }
  }, []);

  const submitLstmThreshold = useCallback(async (threshold: number, lang: Lang) => {
    const id = uploadIdRef.current;
    log.hook('useAnomalyPipeline', 'submitLstmThreshold called', { uploadId: id, threshold, lang });
    if (!id) return;
    // Abort any previous in-flight threshold request before launching a new one.
    if (abortRef.current) {
      log.hook('useAnomalyPipeline', 'submitLstmThreshold aborting previous controller');
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    const epoch = newEpoch();
    dispatch({ type: 'APPLYING_LSTM' });
    try {
      log.hook('useAnomalyPipeline', 'submitLstmThreshold awaiting anomalyApi.lstmThreshold');
      const res = await anomalyApi.lstmThreshold(id, threshold, lang, { signal: controller.signal });
      abortRef.current = null;
      if (epoch !== epochRef.current) {
        log.hook('useAnomalyPipeline', 'submitLstmThreshold stale response, ignoring');
        return;
      }
      log.hook('useAnomalyPipeline', 'submitLstmThreshold ok', { processedCsvFilename: res.processedCsvFilename });
      dispatch({
        type: 'COMPLETE',
        plots: res.plots,
        processedCsvFilename: res.processedCsvFilename ?? null,
      });
    } catch (e) {
      abortRef.current = null;
      if (e instanceof Error && e.name === 'AbortError') {
        // Cancelled by user — cancel flow handles its own dispatch.
        return;
      }
      if (epoch !== epochRef.current) return;
      log.err('useAnomalyPipeline.submitLstmThreshold', e);
      // Same as STL: keep LSTM modal open for retry per Markus L1404-1415.
      dispatch({ type: 'THRESHOLD_VALIDATION_FAILED', error: thresholdErrorPayload(e) });
    }
  }, []);

  const useProcessedAsOriginal = useCallback(async (lang: Lang): Promise<boolean> => {
    const id = uploadIdRef.current;
    log.hook('useAnomalyPipeline', 'useProcessedAsOriginal called', { uploadId: id, lang });
    if (!id) return false;
    const epoch = newEpoch();
    dispatch({ type: 'USE_PROCESSED' });
    try {
      log.hook('useAnomalyPipeline', 'useProcessedAsOriginal awaiting anomalyApi.useProcessed');
      const res = await anomalyApi.useProcessed(id, lang);
      log.hook('useAnomalyPipeline', 'useProcessedAsOriginal ok', { rowCount: res.rowCount });
      if (epoch !== epochRef.current) {
        log.hook('useAnomalyPipeline', 'useProcessedAsOriginal stale response, ignoring');
        return false;
      }
      dispatch({
        type: 'LOADED',
        plots: { original: res.plots.original, slope: res.plots.slope },
        columnName: columnNameRef.current ?? '',
        rowCount: res.rowCount,
      });
      return true;
    } catch (e) {
      log.err('useAnomalyPipeline.useProcessedAsOriginal', e);
      if (epoch !== epochRef.current) return false;
      dispatch({ type: 'PIPELINE_ERROR', error: errorPayloadFromError(e) });
      return false;
    }
  }, []);

  const setParams = useCallback((params: AnomalyParameters) => {
    log.hook('useAnomalyPipeline', 'setParams called');
    dispatch({ type: 'PARAMS_UPDATED', params });
  }, []);

  const reset = useCallback(() => {
    log.hook('useAnomalyPipeline', 'reset called');
    // Bumping epoch invalidates any in-flight responses.
    newEpoch();
    dispatch({ type: 'RESET' });
  }, []);

  const cancel = useCallback(async () => {
    log.hook('useAnomalyPipeline', 'cancel called', { uploadId: uploadIdRef.current });
    // 1. Abort the in-flight fetch — fetch will throw AbortError, caught by
    //    the start() try/catch. The server still processes it, but the client
    //    discards the result (epoch guard also protects against any late reply).
    abortRef.current?.abort();
    abortRef.current = null;
    // 2. Bump epoch so any in-flight response that arrives is silently dropped.
    newEpoch();
    // 3. Hit the backend /cancel-pipeline to release the lock (best-effort,
    //    never throws). uploadIdRef is used here to avoid stale closure over state.
    const uploadId = uploadIdRef.current;
    if (uploadId) {
      await anomalyApi.cancel(uploadId);
    }
    // 4. Transition FSM back to configuring — closes any open threshold modal.
    dispatch({ type: 'CANCELLED' });
  }, []);

  return {
    state,
    dispatch,
    load,
    start,
    submitStlThreshold,
    submitLstmThreshold,
    useProcessedAsOriginal,
    setParams,
    reset,
    cancel,
  };
}
